import { takeEvery, takeLatest, put, call, fork, select } from 'redux-saga/effects'
import { normalize } from 'normalizr'
import { saveAs } from 'file-saver'

import { indicateProgress } from 'api'
import { tokenSelector, userSelector } from 'auth/selectors'
import { LOGOUT } from 'auth/constants'
import { clearEntity } from 'entities/actions'
import { isMobileAgent } from 'shared/responsive-helpers'
import { apiLanguageCodeSelector } from 'i18n/selectors'

import * as constants from './constants'
import * as actions from './actions'
import * as selectors from './selectors'
import * as api from './api'
import { reservationSchema } from './schemas'

export function* locationDetailsSaga() {
    yield takeEvery(constants.FETCH_LOCATION_DETAILS, function*({ locationId }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const locationData = yield call(
                indicateProgress,
                ['locationDetails'],
                api.getLocationData,
                locationId,
                apiLanguageCode
            )

            yield put(actions.receivedLocationDetails(locationData))
        } catch (exception) {}
    })
}

export function* locationRatesSaga() {
    yield takeEvery(constants.FETCH_RATES, function*({ criteria, requestId }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const token = yield select(tokenSelector)
            const response = yield call(
                indicateProgress,
                ['locationRates'],
                api.getRates,
                criteria,
                apiLanguageCode,
                token
            )
            yield put(actions.receivedLocationRates(response, requestId))
        } catch (exception) {
            yield put(actions.failedToFetchRates(criteria, requestId, exception))
        }
    })
}

export function* locationRateDetailsSaga() {
    yield takeEvery(constants.FETCH_RATE_DETAILS, function*({ locationId, rateId, beginTime, endTime }) {
        try {
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const details = yield call(api.getRateDetails, locationId, rateId, beginTime, endTime, apiLanguageCode)
            yield put(actions.receivedRateDetails(details))
        } catch (exception) {}
    })
}

export function* getReservationsSaga() {
    yield takeEvery(constants.GET_RESERVATIONS, function*({ offset, limit, status, requestId }) {
        try {
            const user = yield select(userSelector)
            if (user) {
                const useWaitingList = [constants.STATUS_WAITING_LIST, constants.STATUS_CONFIRMED].indexOf(status) > -1
                const apiLanguageCode = yield select(apiLanguageCodeSelector)
                const response = yield call(
                    useWaitingList ? api.getWaitingListRecordsByAccount : api.getReservations,
                    user.token,
                    offset,
                    limit,
                    status === constants.STATUS_WAITING_LIST ? null : status,
                    apiLanguageCode
                )
                yield put(actions.receivedReservations(response, requestId))
            }
        } catch (exception) {}
    })
}

export function* setAutoRenewSaga() {
    yield takeEvery(constants.SET_AUTO_RENEW, function*({ reservation, autoRenew }) {
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        yield call(indicateProgress, ['setAutoRenew'], api.setAutoRenew, reservation, autoRenew, apiLanguageCode)
        yield put(actions.updatedAutoRenew(normalize({ ...reservation, autoRenew }, reservationSchema)))
    })
}

export function* cancelReservationAmendPolicySaga() {
    yield takeEvery(constants.GET_RESERVATION_CANCEL_AMEND_POLICY, function*({ reservationId }) {
        const { email } = yield select(userSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        const policy = yield call(
            indicateProgress,
            ['reservationCancelAmendPolicy'],
            api.reservationCancelAmendPolicy,
            reservationId,
            email,
            apiLanguageCode
        )

        yield put(actions.reservationCancelAmendPolicyResponse(policy))
    })
}

export function* cancelReservationSaga() {
    yield takeEvery(constants.CANCEL_RESERVATION, function*({ reservationId }) {
        const { email } = yield select(userSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)

        try {
            const response = yield call(
                indicateProgress,
                ['cancelReservation'],
                api.cancelReservationByAccount,
                reservationId,
                email,
                apiLanguageCode
            )

            yield put(actions.cancelReservationResponse(response))
        } catch (error) {
            yield put(actions.cancelReservationResponse(error))
        }
    })
}

export function* clearReservationsSaga() {
    yield takeEvery(LOGOUT, function*() {
        yield put(clearEntity(reservationSchema.key))
    })
}

export function showReceipt(reservationHtml, receiptTab) {
    receiptTab.window.document.write(reservationHtml)
}

export function downloadReceipt(confirmationNumber, reservationHtml) {
    const blob = new Blob([reservationHtml], { type: 'text/html;charset=utf-8' })
    saveAs(blob, `${confirmationNumber}.html`)
}

export function* getReservationReceiptSaga() {
    yield takeEvery(constants.GET_RESERVATION_RECEIPT, function*({ reservationId, receiptTab }) {
        const { confirmationNumber } = yield select(selectors.reservationSelector(reservationId))
        const { email } = yield select(userSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)
        const { reservationHtml } = yield call(api.getReservationReceipt, reservationId, email, apiLanguageCode)

        if (isMobileAgent()) showReceipt(reservationHtml, receiptTab)
        else downloadReceipt(confirmationNumber, reservationHtml)
    })
}

export function* changeVehicleSaga() {
    const apiLanguageCode = yield select(apiLanguageCodeSelector)

    yield takeLatest(constants.CHANGE_VEHICLE, function*({ reservationId, selectedVehicles }) {
        const { token } = yield select(userSelector)
        const response = yield call(
            api.updateReservationVehicleData,
            token,
            reservationId,
            selectedVehicles,
            apiLanguageCode
        )
        yield put(actions.changeVehicleResponse(response))
    })
}

export function* validateWaitingListKeySaga() {
    yield takeEvery(constants.VALIDATE_WAITING_LIST_KEY, function*({ waitingListKey }) {
        const { token } = yield select(userSelector)
        const apiLanguageCode = yield select(apiLanguageCodeSelector)
        try {
            const response = yield call(
                indicateProgress,
                'validateWaitingListKey',
                api.validateWaitingListKey,
                token,
                waitingListKey,
                apiLanguageCode
            )
            yield put(actions.validatedWaitingListKey(waitingListKey, response))
        } catch (error) {
            yield put(actions.failedToValidateWaitingListKey(waitingListKey))
        }
    })
}

export function* addToWaitingListSaga() {
    yield takeEvery(constants.ADD_TO_WAITING_LIST, function*({ rate, grsId }) {
        try {
            const user = yield select(userSelector)
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            const result = yield call(
                indicateProgress,
                'addToWaitingList',
                api.addToWaitingList,
                user,
                rate,
                grsId,
                apiLanguageCode
            )

            yield put(actions.addedToWaitingList(result))
        } catch (error) {}
    })
}

export function* cancelWaitingListSaga() {
    yield takeEvery(constants.CANCEL_WAITING_LIST, function*({ waitingListKey }) {
        try {
            const user = yield select(userSelector)
            const apiLanguageCode = yield select(apiLanguageCodeSelector)
            yield call(
                indicateProgress,
                ['cancelWaitingList', `cancelWaitingList-${waitingListKey}`],
                api.cancelWaitingList,
                user.token,
                waitingListKey,
                apiLanguageCode
            )

            yield put(actions.canceledWaitingList(waitingListKey))
        } catch (error) {}
    })
}

export default function* rootSaga() {
    yield fork(locationDetailsSaga)
    yield fork(locationRatesSaga)
    yield fork(locationRateDetailsSaga)
    yield fork(getReservationsSaga)
    yield fork(setAutoRenewSaga)
    yield fork(cancelReservationAmendPolicySaga)
    yield fork(cancelReservationSaga)
    yield fork(clearReservationsSaga)
    yield fork(getReservationReceiptSaga)
    yield fork(changeVehicleSaga)
    yield fork(validateWaitingListKeySaga)
    yield fork(addToWaitingListSaga)
    yield fork(cancelWaitingListSaga)
}
